import React, { useEffect } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { connect } from 'react-redux'
import BaseYouMayAlsoLikeTile from './BaseYouMayAlsoLikeTile'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import FormatCurrency from 'components/Common/FormatCurrency'
import { pluralizeToString } from 'lib/pluralize'
import { fetchOfferFlightPrice } from 'actions/OfferActions'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import config from 'constants/config'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'

interface Props {
  offer: App.OfferSummary;
  airportCode: string;
}

const PriceWrapper = styled.div`
  display: grid;
  grid-gap: ${rem(16)};
`

function BundledHotelYouMayAlsoLikeTile({ offer, airportCode }: Props) {
  useEffect(() => {
    fetchOfferFlightPrice(offer.id, airportCode)
  }, [offer.id, airportCode])

  const pkg = offer.lowestPricePackage

  let { price, value } = pkg
  const shouldShowValue = pkg.shouldDisplayValue && pkg.value > pkg.price

  const showBadge = !!offer.badge && config.BRAND === 'luxuryescapes'

  const flightPrice = offer.flightPrices[airportCode]

  if (flightPrice) {
    price += flightPrice
    value += flightPrice
  }

  return (
    <BaseYouMayAlsoLikeTile
      image={offer.image}
      location={[offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')}
      title={offer.property.name}
      subtitle={offer.name}
      walledGarden={offer.walledGarden}
    >
      <BaseYouMayAlsoLikeTile.ImageBadges>
        <ProductTypeLabel productType={offer.productType} />
      </BaseYouMayAlsoLikeTile.ImageBadges>
      <BaseYouMayAlsoLikeTile.ContentTags>
        <OfferUrgencyLabels offer={offer} />
      </BaseYouMayAlsoLikeTile.ContentTags>
      <BaseYouMayAlsoLikeTile.PriceDetails>
        <PriceWrapper>
          {showBadge && <OfferBadgeTag badge={offer.badge} />}
          {flightPrice && (
            <div>
              <BodyTextBlock variant="medium" startIcon={<LinePlaneFlyIcon />}>
                {pluralizeToString('night', pkg.duration)}{flightPrice && ' + flights from'}
              </BodyTextBlock>
              <BodyText variant="large" weight="bold" as="span">
                <FormatCurrency value={Math.ceil(price)} />
              </BodyText>
              {shouldShowValue && (
                <Caption variant="medium">Valued up to <FormatCurrency value={Math.ceil(value)} /></Caption>
              )}
            </div>
          )}
          {!flightPrice && (
            <BodyTextBlock variant="medium" weight="bold" startIcon={<LinePlaneFlyIcon />}>
              Hotel + flights packages
            </BodyTextBlock>
          )}
        </PriceWrapper>
      </BaseYouMayAlsoLikeTile.PriceDetails>
    </BaseYouMayAlsoLikeTile>
  )
}

function mapStateToProps(state: App.State) {
  return {
    airportCode: getDefaultAirportCode(state),
  }
}

export default connect(mapStateToProps)(BundledHotelYouMayAlsoLikeTile)
